import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronRight, Save, Search, Cpu, Package, Shapes } from "lucide-react";

/**
 * KioskInspector, admin view that lets you inspect & edit a single kiosk:
 *  - Basis: SKU, name, vendor, image, Bruto/Netto price (read-only)
 *  - Eigenschappen: placement (binnen/buiten/beide) + type (op voet/op balie/hangend/tablet)
 *  - Compatible addons: which addons can be selected (grouped by category)
 *
 * Note: software visibility is driven by use-case + sector + partner allowed_software_slugs,
 * not by per-kiosk linking. Manage that in the "Gebruikssituaties" admin tab.
 */
export default function KioskInspector() {
  const [hardware, setHardware] = useState([]);
  const [addons, setAddons] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [q, setQ] = useState("");
  const [vendorFilter, setVendorFilter] = useState("");
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadAll = async () => {
    const [hwRes, adRes] = await Promise.all([
      api.get("/admin/hardware"),
      api.get("/admin/addons"),
    ]);
    setHardware(hwRes.data || []);
    setAddons(adRes.data || []);
  };

  useEffect(() => { loadAll(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return hardware
      .filter((h) => !vendorFilter || h.vendor_slug === vendorFilter)
      .filter((h) => !needle || (h.sku || "").toLowerCase().includes(needle) || (h.name || "").toLowerCase().includes(needle))
      .sort((a, b) => (a.sku || "").localeCompare(b.sku || ""));
  }, [hardware, q, vendorFilter]);

  const vendors = useMemo(() => Array.from(new Set(hardware.map((h) => h.vendor_slug).filter(Boolean))).sort(), [hardware]);

  const selected = hardware.find((h) => h.id === selectedId);
  useEffect(() => {
    if (selected) {
      setDraft({
        placement: selected.placement || "indoor",
        type: selected.type || "counter",
        image_url: selected.image_url || "",
        purchase_price: selected.purchase_price ?? 0,
        net_price: selected.net_price ?? "",
        compatible_addon_ids: selected.compatible_addon_ids || [],
      });
    } else {
      setDraft(null);
    }
  }, [selectedId]); // eslint-disable-line

  const dirty = useMemo(() => {
    if (!selected || !draft) return false;
    return JSON.stringify({
      placement: selected.placement || "indoor",
      type: selected.type || "counter",
      image_url: selected.image_url || "",
      purchase_price: selected.purchase_price ?? 0,
      net_price: selected.net_price ?? "",
      compatible_addon_ids: selected.compatible_addon_ids || [],
    }) !== JSON.stringify(draft);
  }, [selected, draft]);

  const save = async () => {
    if (!selected || !draft) return;
    setSaving(true);
    try {
      const merged = {
        ...selected,
        ...draft,
        purchase_price: Number(draft.purchase_price) || 0,
        net_price: draft.net_price === "" || draft.net_price == null ? null : Number(draft.net_price),
      };
      const { data } = await api.put(`/admin/hardware/${selected.id}`, merged);
      setHardware((cur) => cur.map((h) => (h.id === selected.id ? data : h)));
      toast.success("Kiosk opgeslagen");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };

  const toggle = (key, id) => {
    setDraft((d) => {
      const arr = new Set(d[key] || []);
      if (arr.has(id)) arr.delete(id); else arr.add(id);
      return { ...d, [key]: Array.from(arr) };
    });
  };

  return (
    <div data-testid="kiosk-inspector" className="grid grid-cols-12 gap-5">
      {/* Sidebar: kiosk list */}
      <aside className="col-span-12 lg:col-span-4 xl:col-span-3">
        <div className="card-surface p-4 sticky top-24">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-white">Kiosken</h2>
            <span className="text-[11px] text-[#A8B3AD]">{filtered.length}/{hardware.length}</span>
          </div>
          <div className="relative mb-2">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#A8B3AD]" />
            <input
              data-testid="kiosk-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Zoek SKU of naam…"
              className="w-full pl-8 pr-2 py-1.5 rounded bg-[#0F1816] border border-[#1F3A2E] text-sm text-white placeholder-[#5d6a64] focus:outline-none focus:border-[#D97742]"
            />
          </div>
          <select
            data-testid="kiosk-vendor-filter"
            value={vendorFilter}
            onChange={(e) => setVendorFilter(e.target.value)}
            className="w-full mb-3 px-2 py-1.5 rounded bg-[#0F1816] border border-[#1F3A2E] text-sm text-white"
          >
            <option value="">Alle leveranciers</option>
            {vendors.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <div className="space-y-1 max-h-[60vh] overflow-y-auto pr-1">
            {filtered.map((h) => (
              <button
                key={h.id}
                data-testid={`kiosk-row-${h.sku}`}
                onClick={() => setSelectedId(h.id)}
                className={`w-full text-left px-2.5 py-2 rounded text-sm transition flex items-center gap-2 ${selectedId === h.id ? "bg-[#1F3A2E] text-white" : "text-[#A8B3AD] hover:bg-[#1A2421]"}`}
              >
                <Cpu className="w-3.5 h-3.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[11px] truncate">{h.sku}</div>
                  <div className="text-[11px] opacity-80 truncate">{h.name}</div>
                </div>
                <ChevronRight className={`w-3.5 h-3.5 shrink-0 ${selectedId === h.id ? "opacity-100" : "opacity-40"}`} />
              </button>
            ))}
          </div>
        </div>
      </aside>

      {/* Detail pane */}
      <section className="col-span-12 lg:col-span-8 xl:col-span-9">
        {!selected && (
          <div className="card-surface p-10 text-center text-[#A8B3AD]" data-testid="kiosk-empty-state">
            <Cpu className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p>Selecteer een kiosk uit de lijst om eigenschappen, compatible software en addons te bekijken.</p>
          </div>
        )}
        {selected && draft && (
          <KioskDetail
            kiosk={selected}
            draft={draft}
            setDraft={setDraft}
            addons={addons}
            toggle={toggle}
            dirty={dirty}
            saving={saving}
            onSave={save}
          />
        )}
      </section>
    </div>
  );
}

function KioskDetail({ kiosk, draft, setDraft, addons, toggle, dirty, saving, onSave }) {
  const placement = draft.placement;
  const type = draft.type;
  const PLACEMENTS = [
    { v: "indoor", label: "Binnen" },
    { v: "outdoor", label: "Buiten" },
    { v: "both", label: "Beide" },
  ];
  const TYPES = [
    { v: "counter", label: "Op balie" },
    { v: "freestanding", label: "Op voet" },
    { v: "wall", label: "Hangend (wand)" },
    { v: "tablet", label: "Tablet" },
  ];

  // group addons by category
  const addonsByCat = useMemo(() => {
    const m = {};
    addons.forEach((a) => { (m[a.category] ||= []).push(a); });
    return m;
  }, [addons]);

  return (
    <div className="space-y-5">
      {/* Header card */}
      <div className="card-surface p-5">
        <div className="flex items-start gap-5 flex-wrap">
          <div className="shrink-0">
            <div className="w-28 h-28 rounded-lg bg-[#0F1816] border border-[#1F3A2E] overflow-hidden">
              {draft.image_url ? (
                <img src={draft.image_url} alt={kiosk.name} className="w-full h-full object-contain" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#5d6a64] text-xs">geen foto</div>
              )}
            </div>
            <input
              data-testid="kiosk-image-url"
              value={draft.image_url}
              onChange={(e) => setDraft((d) => ({ ...d, image_url: e.target.value }))}
              placeholder="Afbeelding-URL…"
              className="mt-2 w-44 px-2 py-1 rounded bg-[#0F1816] border border-[#1F3A2E] text-[11px] text-white placeholder-[#5d6a64] focus:outline-none focus:border-[#D97742]"
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono text-[11px] px-2 py-0.5 rounded bg-[#1F3A2E] text-[#7FD8B8]">{kiosk.sku}</span>
              <span className="text-[11px] text-[#A8B3AD]">{kiosk.vendor_slug}</span>
            </div>
            <h1 className="text-xl font-semibold text-white">{kiosk.name}</h1>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <label className="rounded-lg p-2.5 border border-[#D97742]/30 bg-[#D97742]/5 block">
                <span className="text-[10px] uppercase tracking-wider text-[#A8B3AD]">Bruto (Listprice) €</span>
                <input
                  data-testid="kiosk-bruto-price"
                  type="number" step="0.01"
                  value={draft.purchase_price}
                  onChange={(e) => setDraft((d) => ({ ...d, purchase_price: e.target.value }))}
                  className="mt-0.5 w-full bg-transparent text-xl font-semibold text-white focus:outline-none"
                />
              </label>
              <label className="rounded-lg p-2.5 border border-[#1F3A2E] bg-[#0F1816] block">
                <span className="text-[10px] uppercase tracking-wider text-[#A8B3AD]">Netto (Gold) €</span>
                <input
                  data-testid="kiosk-netto-price"
                  type="number" step="0.01"
                  value={draft.net_price}
                  onChange={(e) => setDraft((d) => ({ ...d, net_price: e.target.value }))}
                  placeholder="—"
                  className="mt-0.5 w-full bg-transparent text-sm font-semibold text-white focus:outline-none"
                />
              </label>
              <PriceTile label="Marge" value={(Number(draft.purchase_price) || 0) - (Number(draft.net_price) || 0)} small accent />
              <PriceTile
                label="Marge %"
                value={(Number(draft.purchase_price) || 0) ? (((Number(draft.purchase_price) || 0) - (Number(draft.net_price) || 0)) / (Number(draft.purchase_price) || 0)) * 100 : 0}
                small accent
                suffix="%"
                decimals={1}
                noEuro
              />
            </div>
          </div>
          <div className="shrink-0">
            <button
              data-testid="kiosk-save-btn"
              disabled={!dirty || saving}
              onClick={onSave}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition ${dirty ? "bg-[#D97742] text-white hover:bg-[#c46939]" : "bg-[#1F3A2E] text-[#5d6a64] cursor-not-allowed"}`}
            >
              <Save className="w-4 h-4" /> {saving ? "Opslaan…" : dirty ? "Opslaan" : "Opgeslagen"}
            </button>
          </div>
        </div>
      </div>

      {/* Eigenschappen */}
      <div className="card-surface p-5">
        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2"><Shapes className="w-4 h-4 text-[#D97742]" /> Eigenschappen</h3>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#A8B3AD] mb-2">Plaatsing</div>
            <div className="flex flex-wrap gap-1.5">
              {PLACEMENTS.map((p) => (
                <button
                  key={p.v}
                  data-testid={`placement-${p.v}`}
                  onClick={() => setDraft((d) => ({ ...d, placement: p.v }))}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${placement === p.v ? "bg-[#D97742] border-[#D97742] text-white" : "border-[#1F3A2E] text-[#A8B3AD] hover:border-[#D97742]/50"}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#A8B3AD] mb-2">Type / montage</div>
            <div className="flex flex-wrap gap-1.5">
              {TYPES.map((t) => (
                <button
                  key={t.v}
                  data-testid={`type-${t.v}`}
                  onClick={() => setDraft((d) => ({ ...d, type: t.v }))}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${type === t.v ? "bg-[#7FD8B8] border-[#7FD8B8] text-[#0a1410]" : "border-[#1F3A2E] text-[#A8B3AD] hover:border-[#7FD8B8]/50"}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Addons */}
      <div className="card-surface p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2"><Package className="w-4 h-4 text-[#D97742]" /> Compatible add-ons</h3>
          <span className="text-[11px] text-[#A8B3AD]">{draft.compatible_addon_ids.length} / {addons.length} gekoppeld</span>
        </div>
        <div className="space-y-4">
          {Object.entries(addonsByCat).map(([cat, list]) => (
            <div key={cat}>
              <div className="text-[10px] uppercase tracking-wider text-[#A8B3AD] mb-1.5">{cat}</div>
              <div className="grid sm:grid-cols-2 gap-1.5">
                {list.map((a) => {
                  const linked = (draft.compatible_addon_ids || []).includes(a.id);
                  return (
                    <button
                      key={a.id}
                      data-testid={`addon-toggle-${a.sku}`}
                      onClick={() => toggle("compatible_addon_ids", a.id)}
                      className={`text-left px-3 py-2 rounded border transition flex items-center gap-2.5 ${linked ? "bg-[#1F3A2E]/40 border-[#7FD8B8]/40" : "bg-[#0F1816] border-[#1F3A2E] hover:border-[#D97742]/40"}`}
                    >
                      <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${linked ? "bg-[#7FD8B8] border-[#7FD8B8]" : "border-[#5d6a64]"}`}>
                        {linked && <span className="text-[#0a1410] text-[10px] font-bold">✓</span>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white truncate" data-testid={`addon-${a.id}-function`}>{a.function_i18n?.nl || a.name}</div>
                        {a.function_i18n?.nl && (
                          <div className="text-[10px] text-[#7A8580] truncate" title={a.name}>{a.name}</div>
                        )}
                        <div className="text-[10px] text-[#A8B3AD] flex items-center gap-1.5 flex-wrap">
                          <span className="font-mono">{a.sku}</span>
                          <span>· €{Number(a.price || 0).toFixed(2)}</span>
                          {a.vendor_slug && <span>· {a.vendor_slug}</span>}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PriceTile({ label, value, highlight, small, accent, suffix = "", decimals = 2, noEuro }) {
  const v = value == null ? null : Number(value);
  const display = v == null ? "—" : `${noEuro ? "" : "€"}${v.toFixed(decimals)}${suffix}`;
  return (
    <div className={`rounded-lg p-2.5 border ${highlight ? "border-[#D97742]/30 bg-[#D97742]/5" : "border-[#1F3A2E] bg-[#0F1816]"}`}>
      <div className="text-[10px] uppercase tracking-wider text-[#A8B3AD]">{label}</div>
      <div className={`mt-0.5 font-semibold ${highlight ? "text-xl text-white" : small ? "text-sm" : "text-base"} ${accent ? "text-[#7FD8B8]" : "text-white"}`}>
        {display}
      </div>
    </div>
  );
}
